import { APIErrorResponse, encodePostRequest, UntransformedResponse } from "./utils/apiUtils";
import { createApi } from "@reduxjs/toolkit/query/react";
import endpoints, { baseURL } from "data/api/v1/endpoints.ts";
import { baseQueryWithRedirect } from "features/api/utils/apiUtils.ts";
import { models } from "types/api/viewModels";
import { GetTrucksResponse, GetTrucksResponseData } from "types/api/trucks/getTrucks.ts";
import { GetTruckDraftsResponse, GetTruckDraftsResponseData } from "types/api/trucks/getTruckDrafts";
import { SaveTruckResponse } from "types/api/trucks/saveTruck";
import { PostTruckSubmitResponse, PostTruckSubmitResponseData } from "types/api/trucks/submitTrucks.ts";

export const truckApiSlice = createApi({
	reducerPath: "truckApi",
	baseQuery: baseQueryWithRedirect(baseURL),
	endpoints: (builder) => ({
		getTrucks: builder.query<GetTrucksResponseData, void>({
			query: () => endpoints.trucks.trucks(),
			transformResponse: (response: GetTrucksResponse) => response?.data,
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			})
		}),
		saveTruck: builder.mutation({
			query: (truck: models["TruckViewModel"]) => ({
				url: endpoints.trucks.trucks(truck.truckId ?? ""),
				method: truck.truckId ? "PUT" : "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(truck)
			}),
			transformResponse: (response: SaveTruckResponse) => response.data,
			transformErrorResponse: (error: any) => ({
				status: error?.status,
				data: {
					errors: error?.data?.errors
				}
			})
		}),
		getTruckDrafts: builder.query<GetTruckDraftsResponseData, string>({
			query: (truckId: string) => endpoints.trucks.getTruckDrafts(truckId),
			transformResponse: (response: GetTruckDraftsResponse) => response?.data,
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			})
		}),
		removeTruck: builder.mutation({
			query: (truckId: string) => ({
				url: endpoints.trucks.trucks(truckId),
				method: "delete"
			})
		}),
		submitTruck: builder.mutation<PostTruckSubmitResponseData, string, APIErrorResponse>({
			query: (truckId) => ({
				url: endpoints.trucks.submitTruck(truckId ?? ""),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				}
			}),
			transformResponse: (response: PostTruckSubmitResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status.toString()
			})
		})
	})
});

export const {
	useGetTrucksQuery,
	useLazyGetTrucksQuery,
	useSaveTruckMutation,
	useLazyGetTruckDraftsQuery,
	useRemoveTruckMutation,
	useSubmitTruckMutation
} = truckApiSlice;
